import React, { useState, useEffect } from "react";
import { BarChart3, Target, AlertTriangle, CheckCircle, TrendingUp, Edit3, Sparkles, RefreshCw, X, Zap, Lock, ShieldCheck } from "lucide-react";
import { UserProfile } from "../types";
import { analyzeGapping, GapAnalysis } from "../utils/distanceCalculator";
import { aiService } from "../services/aiService";
import { recalculateFromKnownDistances } from "../utils/calculations";
import { recordActivationIfEligible, recordProductEvent } from "../services/productEvents";

interface DistancesProps {
  userProfile: UserProfile | null;
  onProfileUpdate: (profile: UserProfile) => Promise<boolean> | void;
}

// Result modal type
interface RecalibrationResult {
  type: 'success' | 'warning' | 'error';
  title: string;
  message: string;
  clubCount?: number;
}

const Distances: React.FC<DistancesProps> = ({ userProfile, onProfileUpdate }) => {
  const [gapAnalysis, setGapAnalysis] = useState<GapAnalysis | null>(null);
  const [editingClub, setEditingClub] = useState<string | null>(null);
  const [editDistance, setEditDistance] = useState<string>("");
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalibrationResult, setRecalibrationResult] = useState<RecalibrationResult | null>(null);
  const [showRecalibConfirm, setShowRecalibConfirm] = useState(false);

  // Create a hash of all distances to detect any changes (not just array reference changes)
  const distanceHash = userProfile?.golfBag
    ?.map(c => `${c.type}:${c.distance || 0}`)
    .sort()
    .join(',') || '';

  useEffect(() => {
    if (userProfile?.golfBag) {
      const analysis = analyzeGapping(userProfile.golfBag);
      setGapAnalysis(analysis);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distanceHash]); // Re-analyze when ANY distance changes

  const handleDistanceEdit = (clubId: string, currentDistance?: number) => {
    setEditingClub(clubId);
    setEditDistance(currentDistance?.toString() || "");
  };

  const saveDistanceEdit = async () => {
    if (!userProfile || !editingClub) return;

    const distance = parseInt(editDistance);
    if (isNaN(distance) || distance <= 0) {
      alert('Please enter a valid distance greater than 0');
      return;
    }

    const editingClubData = userProfile.golfBag.find(c => c.id === editingClub);
    const clubName = editingClubData?.type?.toUpperCase().replace('-', ' ') || 'Club';
    
    const updatedGolfBag = userProfile.golfBag.map(club =>
      club.id === editingClub
        ? { 
            ...club, 
            distance, 
            isDistanceEstimated: false, 
            isManuallySet: true  // CRITICAL: Mark as manually set so recalibration won't overwrite
          }
        : club
    );

    console.log(`💾 Saving ${clubName} distance: ${distance} yards (isManuallySet: true)`);
    // Save and show result
    const success = await onProfileUpdate({
      ...userProfile,
      golfBag: updatedGolfBag
    });

    if (success !== false) {
      console.log(`✅ ${clubName} distance saved successfully`);
      void recordProductEvent('DISTANCES_COMPLETED');
      void recordActivationIfEligible({
        hasCompletedOnboarding: userProfile.hasCompletedOnboarding,
        golfBag: updatedGolfBag,
        clubDistances: userProfile.clubDistances,
      });
    }

    setEditingClub(null);
    setEditDistance("");
  };

  const cancelDistanceEdit = () => {
    setEditingClub(null);
    setEditDistance("");
  };

  const handleAIRecalculate = async () => {
    if (!userProfile || !userProfile.golfBag || userProfile.golfBag.length === 0) {
      setRecalibrationResult({
        type: 'warning',
        title: 'No Clubs Found',
        message: 'Please add clubs to your bag first before recalibrating.'
      });
      return;
    }

    // Need at least one known distance to recalculate from
    const clubsWithDistances = userProfile.golfBag.filter(club => club.distance);
    if (clubsWithDistances.length === 0) {
      setRecalibrationResult({
        type: 'warning',
        title: 'No Distances Set',
        message: 'Please set at least one club distance first, then we can estimate the rest of your bag.'
      });
      return;
    }

    setIsRecalculating(true);
    
    // Track start time for minimum loading duration (3 seconds for perceived value)
    const startTime = Date.now();
    const MIN_LOADING_TIME = 3000;

    // Build known distances object from clubs that have distances
    const knownDistances: { [club: string]: number } = {};
    clubsWithDistances.forEach(club => {
      if (club.distance) {
        knownDistances[club.type] = club.distance;
      }
    });

    // Snapshot current bag to avoid stale closure issues
    const currentGolfBag = [...userProfile.golfBag];

    const applyDistancesToBag = async (calculatedDistances: { [club: string]: number }) => {
      let updatedCount = 0;
      let preservedCount = 0;
      const preservedClubs: string[] = [];
      
      const updatedGolfBag = currentGolfBag.map(club => {
        // RULE 1: NEVER overwrite manually-set distances
        if (club.isManuallySet === true) {
          preservedCount++;
          preservedClubs.push(`${club.type}: ${club.distance}y (manual)`);
          return club;
        }
        
        const calculatedDistance = calculatedDistances[club.type];
        
        // RULE 2: Only update clubs that are explicitly estimated or have no distance
        // Using === true so undefined doesn't slip through
        if (calculatedDistance && calculatedDistance > 0 && (!club.distance || club.isDistanceEstimated === true)) {
          updatedCount++;
          return { 
            ...club, 
            distance: Math.round(calculatedDistance), 
            isDistanceEstimated: true,
            isManuallySet: false
          };
        }
        return club;
      });

      console.log(`📊 Recalibration: ${updatedCount} updated, ${preservedCount} preserved`);

      const success = await onProfileUpdate({
        ...userProfile,
        golfBag: updatedGolfBag
      });

      if (success === false) {
        throw new Error('Failed to save recalibrated distances to server');
      }

      void recordProductEvent('DISTANCES_COMPLETED');
      void recordActivationIfEligible({
        hasCompletedOnboarding: userProfile.hasCompletedOnboarding,
        golfBag: updatedGolfBag,
        clubDistances: userProfile.clubDistances,
      });
      return updatedCount;
    };

    let result: RecalibrationResult;

    // Try AI first, fallback to local calculation if AI fails
    try {
      const calculatedDistances = await aiService.getAIDistanceCalculation({
        userProfile,
        knownDistances,
        conditions: undefined
      });

      const clubCount = await applyDistancesToBag(calculatedDistances);
      result = {
        type: 'success',
        title: `Recalibrated ${clubCount} clubs!`,
        message: 'Your distances have been updated using AI analysis based on your profile and playing style.',
        clubCount
      };

    } catch (aiError) {
      console.warn("AI recalibration failed, using local calculation:", aiError);
      
      // Fallback to local calculation
      try {
        const calculatedDistances = recalculateFromKnownDistances(knownDistances);
        const clubCount = await applyDistancesToBag(calculatedDistances);
        result = {
          type: 'success',
          title: `Recalibrated ${clubCount} clubs!`,
          message: 'Distances updated using standard gapping calculations.',
          clubCount
        };
      } catch (localError) {
        // Both methods failed - rare edge case
        console.error("Both AI and local recalculation failed:", localError);
        result = {
          type: 'error',
          title: 'Recalibration Failed',
          message: 'Couldn\'t recalculate distances right now. Your original distances are safe. You can edit them manually.'
        };
      }
    }
    
    // Ensure minimum loading time for perceived value
    const elapsed = Date.now() - startTime;
    if (elapsed < MIN_LOADING_TIME) {
      await new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME - elapsed));
    }
    
    // Update state
    setIsRecalculating(false);
    setRecalibrationResult(result);
  };

  if (!userProfile) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Distance Analysis</h1>
          <p className="text-gray-600">Analyze your club distances and gapping for optimal performance</p>
        </div>

        {/* No Profile State with Educational Content */}
        <div className="text-center mb-8">
          <BarChart3 className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Get Started with Distance Tracking</h2>
          <p className="text-gray-600 mb-6">Add clubs to your bag and track distances to unlock powerful gapping analysis.</p>
        </div>

        {/* Educational Content */}
        <div className="grid md:grid-cols-2 gap-8">
          <div className="card p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="text-green-500" size={24} />
              Why Track Distances?
            </h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span>Identify gaps in your club set</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span>Make better club selection decisions</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span>Optimize your bag composition</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span>Track improvement over time</span>
              </li>
            </ul>
          </div>

          <div className="card p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="text-blue-500" size={24} />
              Typical Distance Ranges
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="font-medium">Driver</span>
                <span className="text-gray-600">200-280 yards</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="font-medium">3-Wood</span>
                <span className="text-gray-600">180-240 yards</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="font-medium">5-Iron</span>
                <span className="text-gray-600">140-180 yards</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="font-medium">7-Iron</span>
                <span className="text-gray-600">120-160 yards</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="font-medium">9-Iron</span>
                <span className="text-gray-600">100-130 yards</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="font-medium">Pitching Wedge</span>
                <span className="text-gray-600">80-110 yards</span>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Get Started?</h3>
          <p className="text-gray-600 mb-6">Use our Distance Calculator to measure your clubs and start building your distance profile.</p>
          <a 
            href="/club-optimizer?tab=gapping" 
            className="inline-flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors font-semibold"
          >
            <Target size={20} />
            Calculate My Distances
          </a>
        </div>
      </div>
    );
  }

  // Filter out putters from distance display - golfers don't track putter carry
  const clubsWithDistances = userProfile.golfBag
    .filter(club => club.distance && club.distance > 0 && club.type !== 'putter')
    .sort((a, b) => (b.distance || 0) - (a.distance || 0));

  if (clubsWithDistances.length === 0) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Distance Analysis</h1>
          <p className="text-gray-600">Analyze your club distances and gapping for optimal performance</p>
        </div>

        {/* No Data State with Educational Content */}
        <div className="text-center mb-8">
          <BarChart3 className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Get Started with Distance Tracking</h2>
          <p className="text-gray-600 mb-6">Add clubs to your bag and track distances to unlock powerful gapping analysis.</p>
        </div>

        {/* Educational Content */}
        <div className="grid md:grid-cols-2 gap-8">
          <div className="card p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="text-green-500" size={24} />
              Why Track Distances?
            </h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span>Identify gaps in your club set</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span>Make better club selection decisions</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span>Optimize your bag composition</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                <span>Track improvement over time</span>
              </li>
            </ul>
          </div>

          <div className="card p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="text-blue-500" size={24} />
              Typical Distance Ranges
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="font-medium">Driver</span>
                <span className="text-gray-600">200-280 yards</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="font-medium">3-Wood</span>
                <span className="text-gray-600">180-240 yards</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="font-medium">5-Iron</span>
                <span className="text-gray-600">140-180 yards</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="font-medium">7-Iron</span>
                <span className="text-gray-600">120-160 yards</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="font-medium">9-Iron</span>
                <span className="text-gray-600">100-130 yards</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="font-medium">Pitching Wedge</span>
                <span className="text-gray-600">80-110 yards</span>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Get Started?</h3>
          <p className="text-gray-600 mb-6">Use our Distance Calculator to measure your clubs and start building your distance profile.</p>
          <a 
            href="/club-optimizer?tab=gapping" 
            className="inline-flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors font-semibold"
          >
            <Target size={20} />
            Calculate My Distances
          </a>
        </div>
      </div>
    );
  }

  // Calculate which clubs will be affected by recalibration
  const manualClubs = userProfile?.golfBag?.filter(c => c.isManuallySet === true && c.type !== 'putter') || [];
  const estimatedClubs = userProfile?.golfBag?.filter(c => c.isManuallySet !== true && c.type !== 'putter') || [];

  return (
    <div className="max-w-6xl mx-auto px-3 py-4 sm:p-6 space-y-4 sm:space-y-8">
      {/* Recalibration Confirmation Modal */}
      {showRecalibConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-white/20">
                  <RefreshCw className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Recalibrate Distances</h3>
                  <p className="text-white/80 text-sm">Review what will change</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-700 text-sm leading-relaxed">
                Recalibration will update <strong>AI-estimated distances</strong> based on your profile and playing style. 
                Your <strong>manually-entered distances will NOT be changed</strong>.
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <RefreshCw className="w-4 h-4 text-blue-600" />
                    <span className="font-bold text-blue-800 text-sm">Will Update</span>
                  </div>
                  <p className="text-blue-700 text-2xl font-bold">{estimatedClubs.length}</p>
                  <p className="text-blue-600 text-xs">estimated distances</p>
                </div>
                <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="w-4 h-4 text-green-600" />
                    <span className="font-bold text-green-800 text-sm">Protected</span>
                  </div>
                  <p className="text-green-700 text-2xl font-bold">{manualClubs.length}</p>
                  <p className="text-green-600 text-xs">manually set</p>
                </div>
              </div>

              {manualClubs.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                  <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-green-800 text-xs">
                    Protected: {manualClubs.map(c => c.type.replace(/-/g, ' ')).join(', ')}
                  </p>
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowRecalibConfirm(false)}
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowRecalibConfirm(false);
                    handleAIRecalculate();
                  }}
                  className="flex-[2] py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-semibold transition-all shadow-lg"
                >
                  Recalibrate Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recalibration Result Modal */}
      {recalibrationResult && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className={`p-6 ${
              recalibrationResult.type === 'success' 
                ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                : recalibrationResult.type === 'warning'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                : 'bg-gradient-to-r from-red-500 to-rose-600'
            } text-white`}>
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full ${
                  recalibrationResult.type === 'success' 
                    ? 'bg-white/20' 
                    : recalibrationResult.type === 'warning'
                    ? 'bg-white/20'
                    : 'bg-white/20'
                }`}>
                  {recalibrationResult.type === 'success' ? (
                    <Zap className="w-8 h-8" />
                  ) : recalibrationResult.type === 'warning' ? (
                    <AlertTriangle className="w-8 h-8" />
                  ) : (
                    <X className="w-8 h-8" />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{recalibrationResult.title}</h3>
                  {recalibrationResult.clubCount !== undefined && recalibrationResult.clubCount > 0 && (
                    <p className="text-white/80 text-sm">AI-powered analysis complete</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-600 leading-relaxed">{recalibrationResult.message}</p>
              
              {recalibrationResult.type === 'success' && recalibrationResult.clubCount !== undefined && recalibrationResult.clubCount > 0 && (
                <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-100">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">All distances updated</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">Your gapping analysis will now reflect these changes.</p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 pb-6">
              <button
                onClick={() => setRecalibrationResult(null)}
                className={`w-full py-3 px-4 rounded-xl font-semibold transition-all ${
                  recalibrationResult.type === 'success'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : recalibrationResult.type === 'warning'
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                {recalibrationResult.type === 'success' ? 'View Updated Distances' : 'Got It'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Distance Analysis</h1>
        <p className="text-gray-600">Analyze your club distances and gapping for optimal performance</p>
      </div>

      {/* AI Recalculate Button */}
      {userProfile && userProfile.golfBag && userProfile.golfBag.length > 0 && (
        <div className="card p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-2xl">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <Sparkles className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900">AI Distance Optimization</h3>
                <p className="text-sm text-gray-600 mt-0.5">Set one club distance, AI calculates the rest based on your profile</p>
              </div>
            </div>
            <button
              onClick={() => setShowRecalibConfirm(true)}
              disabled={isRecalculating}
              className={`w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                isRecalculating
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-xl'
              }`}
            >
              {isRecalculating ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  AI Recalculate All
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Gapping Score */}
      {gapAnalysis && (
        <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-4 sm:p-6 min-w-0">
          <div className="flex items-start justify-between gap-3 min-w-0">
            <div className="min-w-0 pr-2">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Gapping Score</h2>
              <p className="text-sm text-gray-600 mt-0.5">How well-spaced are your clubs?</p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-3xl sm:text-4xl font-bold text-green-600 leading-none">{gapAnalysis.gappingScore}</div>
              <div className="text-xs sm:text-sm text-gray-500 mt-1">out of 100</div>
            </div>
          </div>
          
          {/* Score Bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-500 ${
                  gapAnalysis.gappingScore >= 80 ? 'bg-green-500' :
                  gapAnalysis.gappingScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${gapAnalysis.gappingScore}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Needs Work</span>
              <span>Good</span>
              <span>Excellent</span>
            </div>
          </div>
        </div>
      )}

      {/* Distance Chart */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 min-w-0 overflow-hidden">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
          <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 mr-2 flex-shrink-0" />
          Club Distances
        </h2>
        
        <div className="space-y-3">
          {clubsWithDistances.map((club, index) => {
            const isEditing = editingClub === club.id;
            const nextClub = clubsWithDistances[index + 1];
            const gap = nextClub ? (club.distance || 0) - (nextClub.distance || 0) : null;
            
            return (
              <div key={club.id} className="space-y-2">
                <div className="flex flex-col gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg min-w-0 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex gap-3 min-w-0 flex-1 items-start">
                    <div className="w-14 sm:w-16 flex-shrink-0 text-xs sm:text-sm font-medium text-gray-600 uppercase leading-tight">
                      {club.type}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-gray-900 break-words">
                        {club.brand} {club.model}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 mt-0.5 flex flex-wrap gap-x-1.5 gap-y-0.5">
                        <span>{club.shaftType}</span>
                        <span className="text-gray-300 hidden sm:inline">•</span>
                        <span>{club.shaftFlex}</span>
                        <span className="text-gray-300">•</span>
                        <span>{club.loft}°</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 w-full min-w-0 sm:flex-row sm:items-center sm:gap-4 sm:w-auto sm:shrink-0">
                    <div className="flex items-center justify-between gap-3 w-full sm:w-auto">
                      {/* Distance */}
                      <div className="text-left sm:text-right min-w-0">
                        {isEditing ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <input
                              type="number"
                              value={editDistance}
                              onChange={(e) => setEditDistance(e.target.value)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                              placeholder="yards"
                              autoFocus
                            />
                            <button
                              onClick={saveDistanceEdit}
                              className="text-green-600 hover:text-green-700"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelDistanceEdit}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div>
                              <div className="text-lg sm:text-xl font-bold text-gray-900">
                                {club.distance} yds
                              </div>
                              <div className={`text-xs flex items-center gap-1 flex-wrap ${club.isManuallySet ? 'text-green-600' : 'text-yellow-600'}`} title={club.isManuallySet ? 'Manually set — protected during recalibration' : 'AI-estimated — will update during recalibration'}>
                                {club.isManuallySet ? (
                                  <><Lock className="w-3 h-3 flex-shrink-0" /> Measured</>
                                ) : (
                                  <><Sparkles className="w-3 h-3 flex-shrink-0" /> Estimated</>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleDistanceEdit(club.id, club.distance)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                              title="Edit distance"
                            >
                              <Edit3 className="w-5 h-5" />
                            </button>
                          </div>
                        )}
                      </div>
                      {/* Distance Bar — desktop only beside controls */}
                      <div className="hidden sm:block w-28 md:w-32 flex-shrink-0">
                        <div className="w-full bg-gray-200 rounded h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded ${club.isManuallySet ? 'bg-green-500' : 'bg-yellow-400'}`}
                            style={{ width: `${Math.min(100, ((club.distance || 0) / 300) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    {/* Full-width bar on small screens */}
                    <div className="sm:hidden w-full min-w-0">
                      <div className="w-full bg-gray-200 rounded h-2 overflow-hidden">
                        <div
                          className={`h-2 rounded ${club.isManuallySet ? 'bg-green-500' : 'bg-yellow-400'}`}
                          style={{ width: `${Math.min(100, ((club.distance || 0) / 300) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Gap indicator */}
                {gap !== null && (
                  <div className={`text-center py-1 text-sm ${
                    gap < 5 ? 'text-red-600' : gap > 20 ? 'text-orange-600' : 'text-green-600'
                  }`}>
                    {gap}yd gap
                    {gap < 5 && ' (too close)'}
                    {gap > 20 && ' (too large)'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recommendations */}
      {gapAnalysis && gapAnalysis.recommendations.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <TrendingUp className="w-6 h-6 text-green-500 mr-2" />
            Recommendations
          </h2>
          
          <div className="space-y-4">
            {gapAnalysis.recommendations.map((rec, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
                <div className="flex-shrink-0 mt-0.5">
                  {rec.includes('gap') ? (
                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                </div>
                <p className="text-gray-700">{rec}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Problems Summary */}
      {gapAnalysis && (gapAnalysis.overlaps.length > 0 || gapAnalysis.gaps.length > 0) && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Overlaps */}
          {gapAnalysis.overlaps.length > 0 && (
            <div className="bg-red-50 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-red-900 mb-4">Club Overlaps</h3>
              <div className="space-y-3">
                {gapAnalysis.overlaps.map((overlap, index) => (
                  <div key={index} className="bg-white p-3 rounded-lg">
                    <div className="font-semibold text-red-800">
                      {overlap.club1} & {overlap.club2}
                    </div>
                    <div className="text-sm text-red-600">
                      Only {overlap.gap} yards apart
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Large Gaps */}
          {gapAnalysis.gaps.length > 0 && (
            <div className="bg-orange-50 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-orange-900 mb-4">Large Gaps</h3>
              <div className="space-y-3">
                {gapAnalysis.gaps.map((gap, index) => (
                  <div key={index} className="bg-white p-3 rounded-lg">
                    <div className="font-semibold text-orange-800">
                      {gap.beforeClub} → {gap.afterClub}
                    </div>
                    <div className="text-sm text-orange-600">
                      {gap.gap} yard gap (too large)
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Distances;

